import { useQuery } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { Pagination } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { PRICE_CLASSES_LIST_QUERY } from '../../graphql/priceClass'
import { getTranslate } from '../../localization'
import { PriceClass } from '../../types/priceClass'
import GeneratePriceClassModal from './GeneratePriceClassModal'
import PriceClassFilters from './PriceClassFilters'
import PriceClassTable from './PriceClassTable'
import useStyles from './styles/index.styles'

export default function PriceClassList() {
  const classes = useStyles()
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [filteredPriceClass, setFilteredPriceClass] = useState<PriceClass[]>([])
  const [generateModalIsOpen, setGenerateModalIsOpen] = useState(false)

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const {
    data: priceClassList,
    loading: loadingForGetPriceClassesList,
    refetch
  } = useQuery<{
    priceClasses: PriceClass[]
  }>(PRICE_CLASSES_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      showLog: true
    }
  })

  useEffect(() => {
    if (priceClassList?.priceClasses) {
      setPage(1)
      let fullPriceClassesList = priceClassList?.priceClasses
      const customSort = (a: PriceClass, b: PriceClass) => {
        const indexA = fullPriceClassesList.indexOf(a)
        const indexB = fullPriceClassesList.indexOf(b)
        return indexA - indexB
      }

      const newPriceClassesList = matchSorter(
        fullPriceClassesList,
        searchInputValue,
        {
          keys: [
            'name',
            'nameTranslatedList.*.value',
            'code',
            'priceActions.*.actionType',
            'priceActions.*.actionAmount.amount',
            'priceActions.*.beneficiary.entityName',
            'priceActions.*.beneficiary.entityType'
          ]
        }
      ).sort(customSort)

      setFilteredPriceClass(newPriceClassesList)
      setcount(newPriceClassesList.length)
    }
  }, [searchInputValue, loadingForGetPriceClassesList, priceClassList])

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Price Class')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Price Classes List')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => setGenerateModalIsOpen(true)}>
          {getTranslate(`Add${isSmallScreen ? '' : ' Price Class'}`)}
        </Button>
      </div>
      <PriceClassFilters
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        loadingForDisableSearchBox={loadingForGetPriceClassesList}
      />
      <PriceClassTable
        data={filteredPriceClass.slice((page - 1) * perPage, page * perPage)}
        refetch={refetch}
        loadingForGetPriceClassesList={loadingForGetPriceClassesList}
      />
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
      {generateModalIsOpen && (
        <GeneratePriceClassModal
          isOpen={generateModalIsOpen}
          onClose={() => setGenerateModalIsOpen(false)}
          refetch={refetch}
        />
      )}
    </div>
  )
}
